//import { captureException } from '@sentry/react';
import { updateDbMaterial, updateDbTexture } from '../../../helpers/idb';
import { addDefaultTexturesToObject } from '../../../helpers/unity/addDefaultTexturesToObject';
import { getTextureFormat, getTextureSize } from '../../../helpers/unityHelper';
import { selectSelectedLabelId } from '../../../redux/slicers/configurator/configurator.slicer';
import { getAppState } from '../../../redux/store';
import { configuratorUnityContext } from './configurator.config';
import {
  addDefaultTextureName,
  isGlassMaterial,
  resetPathIfIdIsNotPresent,
  sendDataToAddRenderers,
} from '../utils';
import { LogConsole, LogCurator } from '../../../helpers/logEvents';
import { createSentryUnityModule } from '../../container/unityContainer';
// import { fetchMaterialMemoized } from './objectPropertiesMethods';

export const sendUnityEvent = (moduleName, methodName, data) => {
  if (window.LFApp.unity.actionsDebuggingEnabled) {
    console.log('UNITY_SEND_ARGS', moduleName, methodName, data);
  }
  try {
    const d = data && typeof data !== 'string' ? JSON.stringify(data) : data;
    configuratorUnityContext.send(moduleName, methodName, d);
  } catch (e) {
    console.log('UNITY_SEND_ARGS', moduleName, methodName, data);
    //captureException(e);
    throw e;
  }
};

export const prepareTextureData = async (texture, mapType) => {
  const textureDbData = await updateDbTexture(texture);
  const textureUrl = textureDbData?.file || texture.texture_file || texture.low_texture_file;
  if (!textureUrl) return null;
  try {
    const { textureHeight, textureWidth } = await getTextureSize(texture, textureUrl);
    const data = {
      id: texture.id,
      textureUrl: textureUrl,
      mapType,
      version: texture.version,
      skuId: texture.sku_id,
      content: texture.description,
      textureWidth,
      textureHeight,
      textureFormat: getTextureFormat(textureDbData),
      textureName: textureDbData?.name || texture?.name,
      isGeneratedMap: false,
      textureRotation: parseInt(texture.rotation) || 0,
    };

    return data;
  } catch (error) {
    //captureException(error);
    console.error(error);
    return null;
  }
};

const LabelsController = {
  highlightObject: ({ name }) => {
    sendUnityEvent('LabelsController', 'HighlightObjectByName', JSON.stringify([name]));
  },
  disableHighlightObject: () => {
    sendUnityEvent('LabelsController', 'HighlightObjectByName', JSON.stringify([]));
  },
  highlightObjects: (objectNames) => {
    sendUnityEvent('LabelsController', 'HighlightObjectByName', JSON.stringify(objectNames));
  },
  AddRenderersToGroup: (labelId, objectNames) => {
    const data = sendDataToAddRenderers({ objectNames });
    sendUnityEvent(
      'LabelsController',
      'AddRenderersToGroup',
      JSON.stringify({ groupName: labelId, propData: data })
    );
  },
  UnlinkRenderersFromLabels: (objectNames) => {
    const data = objectNames.map((val) => {
      return {
        name: val,
        status: false,
      };
    });
    sendUnityEvent(
      'LabelsController',
      'UnlinkRenderersFromLabels',
      JSON.stringify({ propData: data })
    );
  },
  DeleteObject: (objectName) => {
    sendUnityEvent('LabelsController', 'DeleteObjectReceiver', objectName);
  },
};

const MainController = {
  SelectObjectReceiver({ objectNames }) {
    const itemsToSelect = objectNames.map((objectName) => ({
      objectName: objectName,
      ObjectSelectionMode: 'Style',
      clickedObjectName: '',
    }));
    sendUnityEvent('MainController', 'SelectObjectReceiver', JSON.stringify(itemsToSelect));
  },
  DisableGameObject(objectName) {
    const data = {
      deleted: true,
      deletionType: 'Inactive',
      objectName,
    };
    sendUnityEvent('MainController', 'DisableGameObject', JSON.stringify(data));
  },
  DisableListOfGameObjects(objects) {
    const data = objects.map((name) => {
      return {
        deleted: true,
        deletionType: 'Inactive',
        objectName: name,
      };
    });
    sendUnityEvent('MainController', 'DisableListOfGameObjects', JSON.stringify(data));
  },
  UnloadLifestyleReceiver() {
    sendUnityEvent('MainController', 'UnloadLifestyleReceiver');
  },
  UpdateConfiguratorPropsInfoReceiver(data) {
    console.warn('UpdateConfiguratorPropsInfoReceiver', data);
    sendUnityEvent('MainController', 'UpdateConfiguratorPropsInfoReceiver', JSON.stringify(data));
  },
  GetClickedObjectNameReceiver() {
    sendUnityEvent('MainController', 'GetClickedObjectNameReceiver');
  },
};

window.DisableLogInUnity = (enabled) => {
  sendUnityEvent('MainController', 'DisableLogInUnity', JSON.stringify(enabled));
};

const RoomModule = {
  GetRoomInfo(type = 'normal') {
    // @type 'normal', 'autosave'
    // triggers get room info -> room info will come back in the callback -> window.GetRoomInfo
    sendUnityEvent('RoomModule', 'GetRoomInfo', type);
  },
  CreateRoomOnConfiguratorReceiver(data) {
    sendUnityEvent('RoomModule', 'CreateRoomOnConfiguratorReceiver', JSON.stringify(data));
  },
  CreateProjectOnConfiguratorReceiver(data) {
    sendUnityEvent('RoomModule', 'CreateProjectOnConfiguratorReceiver', JSON.stringify(data));
  },
  InstantiatePropReceiever(prop) {
    console.log(prop, 'InstantiatePropReceiever');
    sendUnityEvent('RoomModule', 'InstantiatePropReceiever', JSON.stringify(prop));
  },
  GetRoomInfoNormalReceiver() {
    console.log('GetRoomInfoNormalReceiver');
    sendUnityEvent('RoomModule', 'GetRoomInfoNormalReceiver');
  },
  GetLoadedLifeStyleInfoReceiver() {
    sendUnityEvent('RoomModule', 'GetLoadedLifeStyleInfoReceiver');
  },
};

const ShortcutsModule = {
  DeleteObjectReceiver() {
    sendUnityEvent('ShortcutsModule', 'DeleteObjectReceiver');
  },
};

const ConfiguratorTransformController = {
  UnselectAllObjects() {
    sendUnityEvent('ConfiguratorTransformController', 'UnselectAllObjects');
  },
};

const configuratorBridge = {
  MainController,
  RoomModule,
  LabelsController,
  ShortcutsModule,
  ConfiguratorTransformController,
  InitializeConfigurator: (data) => {
    sendUnityEvent('ConfiguratorModule', 'InitializeConfigurator', JSON.stringify(data));
  },
  InitializeCamera: (cameraData) => {
    sendUnityEvent('ConfiguratorBridge', 'InitializeCamera', JSON.stringify(cameraData));
  },
  LoadProp: ({
    id,
    propName,
    locationUrl,
    roomLightDatas,
    main_asset_name,
    blender_id,
    isExclusiveProp,
    modelType,
    configuratorPropMaxCount,
    ...rest
  }) => {
    const propData = {
      id,
      propName,
      locationUrl,
      roomLightDatas,
      main_asset_name,
      blender_id,
      isExclusiveProp,
      configuratorPropMaxCount,
      type: 'ConfiguratorLibraryProp',
      ...(modelType ? { modelType } : {}),
    };

    console.log('InstantiatePropFromLibrary Event', JSON.parse(JSON.stringify(propData)));
    sendUnityEvent('ConfiguratorModule', 'InstantiatePropFromLibrary', JSON.stringify(propData));
  },
  SetupCameraSettings: (camera) => {
    console.warn('SetupCameraSettings', camera);
    sendUnityEvent('ConfiguratorBridge', 'SetupCameraSettings', JSON.stringify(camera));
  },
  GetCurrentCameraModel: () => {
    console.warn('GetCurrentCameraModel');
    sendUnityEvent('ConfiguratorBridge', 'GetCurrentCameraModel', '');
  },
  GetCameraUpdatedValues: () => {
    console.warn('GetCameraUpdatedValues');
    sendUnityEvent('ConfiguratorBridge', 'GetCameraUpdatedValues', '');
  },
  UpdateSelectedTransform: ({ propertyName, axes: { x, y, z }, objectId }) => {
    const data = {
      SelectedTransformId: objectId,
      TransformPropertyName: propertyName,
      TransformPropertyValue: { x, y, z },
    };
    console.warn('UpdateSelectedTransform', data);
    sendUnityEvent('ConfiguratorBridge', 'UpdateSelectedTransform', JSON.stringify(data));
  },
  UpdateCameraProperties: ({ id, propertyName, value }) => {
    const data = {
      CameraId: id,
      PropertyName: propertyName,
      PropertyValue: value,
    };
    console.warn('UpdateCameraProperties', data);
    sendUnityEvent('ConfiguratorBridge', 'UpdateCameraProperties', JSON.stringify(data));
  },
  async UpdateTexture({ texture, mapType, labelId }) {
    const textureData = await prepareTextureData(texture, mapType);
    if (!textureData) return false;
    const data = {
      ...textureData,
      labelName: labelId,
    };
    sendUnityEvent('ConfiguratorBridge', 'UpdateTexture', JSON.stringify(data));
    return true;
  },
  UpdateColor({ color, labelId }) {
    const data = {
      color_value: color,
      labelName: labelId,
    };
    sendUnityEvent('ConfiguratorBridge', 'UpdateColor', JSON.stringify(data));
  },
  async UpdateMaterial({ material, labelId }) {
    const materialDbData = await updateDbMaterial(material);
    const data = {
      id: material.id,
      materialUrl: materialDbData?.file || material.material_image,
      labelName: labelId,
    };
    sendUnityEvent('ConfiguratorBridge', 'UpdateMaterial', JSON.stringify(data));
  },
  ResetConfiguratorReceiver() {
    sendUnityEvent('ConfiguratorBridge', 'ResetConfiguratorReceiver');
  },
};

window.configuratorBridge = configuratorBridge;

const configuratorModule = {
  CreateConfiguratorReceiver(data) {
    console.log('UNITY_DEBUG -> init config', data);
    sendUnityEvent('ConfiguratorModule', 'CreateConfiguratorReceiver', JSON.stringify(data));
  },
  InitializeConfigurator(data) {
    console.log('UNITY_DEBUG -> init config', data);
    sendUnityEvent('ConfiguratorModule', 'InitializeConfigurator', JSON.stringify(data));
  },
  ResetConfiguratorReceiver() {
    LogCurator('ResetConfiguratorReceiver');
    sendUnityEvent('ConfiguratorModule', 'ResetConfiguratorReceiver');
  },
  GetConfiguratorJson() {
    sendUnityEvent('ConfiguratorModule', 'GetConfiguratorJson');
  },
  InstantiatePropReceiever(prop) {
    LogCurator('InstantiatePropReceiever', prop);
    sendUnityEvent('ConfiguratorModule', 'InstantiatePropReceiever', JSON.stringify(prop));
  },
  InstantiateGroupProp(finalJson) {
    LogCurator('InstantiateGroupProp', finalJson);
    sendUnityEvent('ConfiguratorModule', 'InstantiateGroupProp', JSON.stringify(finalJson));
  },
  OnCallBase64ImageDataReceiver() {
    sendUnityEvent('ConfiguratorMaterialModule', 'OnCallBase64ImageDataReceiver');
  },
  ThreeSixtyConfiguratorStatusReceiver(flag) {
    sendUnityEvent('ConfiguratorModule', 'ThreeSixtyConfiguratorStatusReceiver', flag);
  },
  async SetMaterialPropertiesOnGivenObject({ objectName, objectData, isDefaultData = false }) {
    const objectWithDefaultTextures = await addDefaultTexturesToObject(objectData, true);
    if (objectWithDefaultTextures?.changedMapsPath) {
      const defaultUpdatedPath = addDefaultTextureName(objectWithDefaultTextures?.changedMapsPath);
      const changedMapsPath = resetPathIfIdIsNotPresent(defaultUpdatedPath);
      objectWithDefaultTextures.changedMapsPath = changedMapsPath;
    }
    sendUnityEvent(
      'ConfiguratorMaterialModule',
      'SetMaterialPropertiesOnGivenObject',
      JSON.stringify({
        objectName,
        objectData: objectWithDefaultTextures,
        isDefaultData,
      })
    );
  },
  projectModule: {
    ApplyMaterialReceiver(materialData) {
      console.warn('ApplyMaterialReceiver', materialData);
      sendUnityEvent(
        'ConfiguratorProjectModule',
        'ApplyMaterialReceiver',
        JSON.stringify(materialData)
      );
    },
    SendRoomLoadedCompletelyInfo() {
      sendUnityEvent('ConfiguratorProjectModule', 'SendRoomLoadedCompletelyInfo');
    },
  },
  SendBase64ImageDataByObjectNameReceiver(objectName) {
    sendUnityEvent(
      'ConfiguratorMaterialModule',
      'SendBase64ImageDataByObjectNameReceiver',
      objectName
    );
  },
  GetBase64DataUsingTextureName(data) {
    sendUnityEvent(
      'ConfiguratorMaterialModule',
      'GetBase64DataUsingTextureName',
      JSON.stringify(data)
    );
  },
  // UpdateEnvironmentColorReceiver(color) {
  //   sendUnityEvent(
  //     'ConfiguratorEnvironmentController',
  //     'UpdateEnvironmentColorReceiver',
  //     color
  //   );
  // },
  SetBackGroundImagePropertiesReceiver() {
    sendUnityEvent('ConfiguratorEnvironmentController', 'SetBackGroundImagePropertiesReceiver');
  },
  // ResetBackgroundReceiver() {
  //   sendUnityEvent(
  //     'ConfiguratorEnvironmentController',
  //     'ResetBackgroundReceiver'
  //   );
  // }
};

export const applyStyleLastInfo = {
  labelId: null,
  lastApplied: null,
};

export const sentryModule = createSentryUnityModule('SentryModule', sendUnityEvent);

export const configuratorMaterialModule = {
  GetObjectDataByLabelId({ labelId }) {
    sendUnityEvent(
      'ConfiguratorMaterialModule',
      'GetObjectDataByLabelId',
      JSON.stringify({ labelName: labelId })
    );
  },
  async ApplyStyleReceiver({
    material,
    texture,
    color,
    option,
    labelId,
    baseMaterialData,
    defaultObjectData,
    mapType,
  }) {
    labelId = labelId || selectSelectedLabelId(getAppState());
    const data = {
      labelName: labelId,
      ...(defaultObjectData && { defaultObjectData }),
    };
    if (material) {
      const materialDbData = await updateDbMaterial(material);
      if (!materialDbData) return false;
      data.materialDataModel = {
        id: materialDbData.id,
        materialUrl: materialDbData?.file,
        isGlassMaterial: isGlassMaterial({ material: materialDbData }),
      };
      applyStyleLastInfo.lastApplied = 'material';
    }
    if (texture) {
      const textureData = await prepareTextureData(texture, mapType);
      if (!textureData) return false;
      if (texture.isGeneratedMap) {
        textureData.isGeneratedMap = true;
      }
      data.textureDataModel = textureData;
      applyStyleLastInfo.lastApplied = 'texture';
    }
    if (color) {
      data.colorDataModel = {
        color_value: color,
        sendObjectData:
          applyStyleLastInfo.labelId !== labelId || applyStyleLastInfo.lastApplied !== 'color',
      };
      applyStyleLastInfo.lastApplied = 'color';
    }
    if (baseMaterialData) {
      const objectWithDefaultTextures = await addDefaultTexturesToObject(baseMaterialData, true);
      if (objectWithDefaultTextures?.changedMapsPath) {
        const defaultUpdatedPath = addDefaultTextureName(
          objectWithDefaultTextures?.changedMapsPath
        );
        const changedMapsPath = resetPathIfIdIsNotPresent(defaultUpdatedPath);
        objectWithDefaultTextures.changedMapsPath = changedMapsPath;
      }
      data.baseMaterialData = objectWithDefaultTextures;
    }
    applyStyleLastInfo.labelId = labelId;
    // if (option) {
    //   const sceneId = selectSelectedSceneId(getAppState());
    //   dispatcher(configuratorActions.setSelectedOption({ data: option, sceneId, labelId }));
    // }
    if (data.defaultObjectData) {
      const dataToSend = await addDefaultTexturesToObject(data.defaultObjectData, true);
      if (dataToSend?.changedMapsPath) {
        const defaultUpdatedPath = addDefaultTextureName(dataToSend?.changedMapsPath);
        const changedMapsPath = resetPathIfIdIsNotPresent(defaultUpdatedPath);
        dataToSend.changedMapsPath = changedMapsPath;
      }
      // const materialId = dataToSend?.loadedMaterialId;
      // if (materialId) {
      //   const isNumber = /^\d+$/.test(materialId);
      //   if (isNumber) {
      //     const material = await fetchMaterialMemoized(materialId);
      //     if (material?.filteroptions?.length) {
      //       dataToSend.filterOptions = material.filteroptions;
      //     }
      //   }
      // }
      data.defaultObjectData = dataToSend;
    }
    console.warn('ApplyStyleReceiver', data);
    sendUnityEvent('ConfiguratorMaterialModule', 'ApplyStyleReceiver', JSON.stringify(data));
    return true;
  },
};

window.configuratorModule = configuratorModule;

export { configuratorBridge, LabelsController, configuratorModule };
